import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { ButtonInLink } from '@hivebrite/design-system/ButtonInLink';

import {
  LIST_TYPES,
  type ListProps,
} from 'hb-builder/components/BuilderComponents/dynamic/List/ListLayouts/types';
import { getUrlToIndexPage } from 'hb-builder/components/BuilderComponents/dynamic/List/ListLayouts/utils/urlToIndex';
import type { Event } from 'hb-builder/components/BuilderComponents/dynamic/types/event';
import ListEventLoader from 'hb-builder/components/common/LoadingComponents/ListEventLoader';

import ListItem from './ListItem';

export type EventsWrapperProps = Pick<
  ListProps,
  'item' | 'ids' | 'navigate' | 'whiteLabelUrl'
> & {
  events: Event[];
  isBackOffice: boolean;
  isLoading: boolean;
};

const EventsWrapper = ({
  item,
  events,
  ids,
  isBackOffice,
  isLoading,
  navigate,
  whiteLabelUrl,
}: EventsWrapperProps) => {
  if (isLoading) {
    return <ListEventLoader />;
  }

  const { buttonLink, dynamicAttributes } = item.attributes || {};

  const linkProps = {
    ...(!isBackOffice && {
      rel: 'noreferrer noopener',
      target:
        buttonLink && dynamicAttributes?.button?.newPage ? '_blank' : '_self',
      to:
        buttonLink ||
        getUrlToIndexPage({
          topicId: ids?.topic || null,
          type: LIST_TYPES.EVENTS,
          whiteLabelUrl,
        }),
    }),
  };

  return (
    <Wrapper>
      <StyledBox>
        {events.map((event) => (
          <ListItem
            dynamicAttributes={dynamicAttributes}
            ids={ids}
            event={event}
            isBackOffice={isBackOffice}
            key={event.id}
            navigate={navigate}
          />
        ))}
      </StyledBox>
      {dynamicAttributes?.button?.display && (
        <ButtonWrapper>
          <ButtonInLink linkProps={linkProps} variant="primary">
            {item.buttonContent || s__('Builder|See all events')}
          </ButtonInLink>
        </ButtonWrapper>
      )}
    </Wrapper>
  );
};

export default EventsWrapper;

const Wrapper = styled(Box).attrs({
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'column',
  pt: {
    _: 6,
    l: 8,
  },
  width: '100%',
})``;

const StyledBox = styled(Box).attrs({
  /*
    Design wants the same rows height inside the list
    With the display grid and the grid-auto-rows below we can achieve this.
    Don't need this in mobile because we don't have this notion of spacing between rows
  */
  display: {
    _: 'flex',
    m: 'grid',
  },
  flexDirection: 'column',
  width: '100%',
})`
  /* Take the maximum row's height and apply the same to all children */
  grid-auto-rows: 1fr;
`;

const ButtonWrapper = styled(Box).attrs({
  pb: 6,
})`
  a {
    text-decoration: none;
  }
`;
